import React, { useEffect, useState } from "react";
import { useTheme } from "../config/ThemeContext";
import {
  TEAMSIDE_HOME,
  TEAMSIDE_AWAY,
  SPECIAL_EVENT_YELLOW_CARD,
  SPECIAL_EVENT_RED_CARD,
  SPECIAL_EVENT_YELLOW_RED,
  SPECIAL_EVENT_DOUBLE_RED,
  SPECIAL_EVENT_DELAY_PENALTY
} from "../config/matchEventConfig";

/**
 * Props:
 * - matchId
 * - team ("teamA" or "teamB")
 * - teamName (e.g. "Spain")
 * - matchState (Redis state)
 * - currentSet (set number)
 * - socket (WebSocket instance)
 * - onClose (function to close the panel)
 */
const PenaltyPanel = ({ matchId, team, teamName, matchState, currentSet, socket, onClose }) => {
  const theme = useTheme();
  const [targetId, setTargetId] = useState("");
  const [penaltyType, setPenaltyType] = useState("");

  // Combine players and staff
  const rotation = matchState.sets[currentSet][`rotation${team === "teamA" ? "A" : "B"}`] || [];
  const players = Object.entries(matchState.playerMap[team] || {}).map(([id, p]) => ({
    id,
    name: p.shirtName,
    number: p.shirtNumber,
    source: rotation.includes(parseInt(id)) ? "🏐 On Court" : "Bench"

  }));

  const staff = (matchState.teams[team]?.staff || []).map(staff => ({
    id: staff.id,
    name: staff.name,
    source: staff.role || "Staff"
  }));

  const penalizable = [...players, ...staff];

const handleSubmit = () => {
  if (!targetId || !penaltyType || !socket || !matchState) return;

  const teamSymbol = team === "teamA" ? TEAMSIDE_HOME : TEAMSIDE_AWAY;
  const opposingTeam = team === "teamA" ? "teamB" : "teamA";
  const playerMap = matchState.playerMap?.[team] || {};
  const player = playerMap[targetId];
  const isStaff = !player; // If not found in playerMap, assume it's staff
 const staffMember = staff.find(s => s.id === targetId);
  const shirtNumber = player?.shirtNumber?.toString().padStart(2, "0") ?? "00";
  const codeMap = {
    YELLOW: SPECIAL_EVENT_YELLOW_CARD,
    RED: SPECIAL_EVENT_RED_CARD,
    YELLOW_RED: SPECIAL_EVENT_YELLOW_RED,
    DOUBLE_RED: SPECIAL_EVENT_DOUBLE_RED,
    DELAY: SPECIAL_EVENT_DELAY_PENALTY
  };

  const eventType = codeMap[penaltyType];
  const actionCode = `${teamSymbol}${eventType}${shirtNumber}`;

socket.emit("scoutingUpdate", {
  matchId,
  scoutingEntry: {
    code: actionCode,
    setNumber: currentSet,
    teamSide: team,
    playerId: isStaff ? null : targetId,
    eventType, // "Y", "R", etc.
	 eventDetails: isStaff ? {
      staffId: staffMember?.id,
      staffName: staffMember?.name,
      staffRole: staffMember?.source, // or staffMember.role
    } : null,
  }
});


  onClose();
};


  // ESC key closes panel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="max-w-xl mx-auto mt-6 bg-white border rounded-md shadow p-6">
      <h3 className="text-xl font-semibold mb-3 text-center text-gray-800">
        Assign Penalty – {teamName}
      </h3>

      {/* Dropdown */}
      <div className="mb-4">
        <label className="block font-medium mb-1">Select Player or Staff:</label>
        <select
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          className="w-full border rounded p-2"
        >
          <option value="">-- Select --</option>
          {penalizable.map(p => (
            <option key={p.id} value={p.id}>
              {p.number ? `#${p.number} ${p.name}` : p.name} ({p.source})
            </option>
          ))}
        </select>
      </div>

      {/* Penalty Type Buttons */}
      <div className="flex justify-center gap-4 flex-wrap mb-6">
        {[
          { code: "YELLOW", label: "🟨 Yellow" },
          { code: "RED", label: "🟥 Red" },
          { code: "YELLOW_RED", label: "🟨+🟥 Expulsion" },
          { code: "DOUBLE_RED", label: "🟥+🟥 Disqualification" },
          { code: "DELAY", label: "⏱️ Delay Penalty" },
        ].map(p => (
          <button
            key={p.code}
            onClick={() => setPenaltyType(p.code)}
            className={`px-3 py-2 rounded border ${penaltyType === p.code ? "ring-2 ring-blue-400 bg-blue-100" : "hover:bg-gray-100"}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-4">
        <button
          onClick={handleSubmit}
          disabled={!targetId || !penaltyType}
          className={`${theme.buttons.primary} focus:outline-none`}
        >
          Submit
        </button>
        <button
          onClick={onClose}
          className={`${theme.buttons.secondary} focus:outline-none`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default PenaltyPanel;
